import axiosInstance from '../plugin/axios';

interface FetchOrdersParams {
  startDate: string;
  endDate: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

interface OrderResponse {
  id: number;
  target: string;
  wanted: string;
  work: string;
  type: string;
  amount: number;
  totalVehicle: number;
  remainingVehicle: number;
}

interface FetchOrdersDriverParams {
  startDate: string;
  endDate: string;
  name?: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

interface DriverOrderResponse {
  id: number;
  target: string;
  name: string;
  work: string;
  amount: number;
  totalVehicle: number;
  remainingVehicle: number;
  preference: string;
}

interface GetCompanyNameResponse {
  name: string;
}

// orderCompanyStep2Dto 인터페이스 정의
interface OrderCompanyStep2Dto {
  wanted: string;
  targetDate: string;
  targetTime: string;
  work: string;
  totalVehicle: number; // 문자열이 아닌 숫자형만 허용
  amount: number; // 문자열이 아닌 숫자형만 허용
}

// 서버 응답 인터페이스 정의
interface ServerResponse {
  statusCode: number;
  message: string;
}

interface UpdateOrderData {
  wanted: string;
  targetDate: string;
  targetTime: string;
  work: string;
  totalVehicle: number;
  amount: number;
}

// driver response data
interface DetailOrderListDriverDtoRx {
  id: number; // 발주 내역 아이디
  name: string; // 기업명
  wanted: string; // 수배 차량
  targetDate: number; // 도착 날짜
  targetTime: string; // 도착 시
  work: string; // 작업 형태
  amount: number; // 금액
  fuelSupport: boolean; // 유류 지원
  totalVehicle: number; // 총 대수
  remainingVehicle: number; // 남은 대수
  notice: string; // 공지 사항
  standrardTask: string; // 작업 기준
}

interface ObtainsOrderDto {
  vehicle: string[];
}

// API로부터 반환되는 각 수주 내역의 타입 정의
interface DetailOrderObtainDto {
  orderId: number; // 발주 아이디
  obtainId: number; // 수주 아이디
  target: string; // 도착 시간
  vehicle: string; // 차량 번호
  obtainCreatedAt: string; // 수주 시간
}

interface CreateObtainNotificationDto {
  orderId: number; // 발주 아이디
  obtainId: number[]; // 수주 아이디 목록
  target?: string; // 도착시 (optional)
  text?: string; // 안내 문구 (optional)
  approval?: boolean; // 승인 여부 (optional)
}

export const fetchCompanyOrders = async (
  params: FetchOrdersParams,
): Promise<OrderResponse[]> => {
  try {
    const {
      startDate,
      endDate,
      offset = 0,
      limit = 1000,
      sortBy = 'target',
      order = 'desc',
    } = params;

    const response = await axiosInstance.get<OrderResponse[]>(
      '/api/order/company/list',
      {
        params: {
          startDate,
          endDate,
          offset,
          limit,
          sortBy,
          order,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error('발주 내역 데이터를 불러오는 데 실패했습니다.', error);
    throw new Error('발주 내역 데이터를 불러오는 데 실패했습니다.');
  }
};

export const fetchDriverOrders = async (
  params: FetchOrdersDriverParams,
): Promise<DriverOrderResponse[]> => {
  try {
    const {
      startDate,
      endDate,
      name,
      offset = 0,
      limit = 1000,
      sortBy = 'target',
      order = 'desc',
    } = params;

    const response = await axiosInstance.get<DriverOrderResponse[]>(
      '/api/order/driver/list',
      {
        params: {
          startDate,
          endDate,
          name,
          offset,
          limit,
          sortBy,
          order,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error('발주 내역 데이터를 불러오는 데 실패했습니다.', error);
    throw new Error('발주 내역 데이터를 불러오는 데 실패했습니다.');
  }
};

export const fetchDriverOrdersPreferred = async (
  params: FetchOrdersDriverParams,
): Promise<DriverOrderResponse[]> => {
  try {
    const {
      startDate,
      endDate,
      name,
      offset = 0,
      limit = 1000,
      sortBy = 'target',
      order = 'desc',
    } = params;

    const response = await axiosInstance.get<DriverOrderResponse[]>(
      '/api/order/driver/preferred',
      {
        params: {
          startDate,
          endDate,
          name,
          offset,
          limit,
          sortBy,
          order,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error('발주 내역 데이터를 불러오는 데 실패했습니다.', error);
    throw new Error('발주 내역 데이터를 불러오는 데 실패했습니다.');
  }
};

export const getCompanyName = async (): Promise<GetCompanyNameResponse> => {
  try {
    const response = await axiosInstance.get<GetCompanyNameResponse>(
      '/api/order/company/order/step1',
    );
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('회사 데이터를 불러오는 데 실패했습니다.');
  }
};

// PUT API 함수 정의
export const ordersRegistrations = async (
  orderData: OrderCompanyStep2Dto[],
): Promise<ServerResponse> => {
  try {
    const response = await axiosInstance.post<ServerResponse>(
      '/api/order/company/order/step2',
      orderData,
    );
    return response.data;
  } catch (error: any) {
    console.error(
      'Error updating order step 2:',
      error?.response?.data || error.message,
    );
    throw error;
  }
};

// order driver details
export const fetchDriverOrderDetail = async (
  driverId: number,
): Promise<DetailOrderListDriverDtoRx> => {
  try {
    const response = await axiosInstance.get<DetailOrderListDriverDtoRx>(
      `/api/order/driver/detail/${driverId}`,
    );
    return response.data; // 서버로부터 받은 데이터를 반환
  } catch (error) {
    console.error('Error fetching driver detail:', error);
    throw error; // 에러 발생 시 에러를 반환
  }
};

// order compnay details
export const fetchOrderDetail = async (orderId: number) => {
  try {
    const response = await axiosInstance.get(
      `/api/order/company/list/${orderId}`,
    );
    return response.data; // 서버로부터 받은 데이터를 반환
  } catch (error) {
    console.error('Error fetching order detail:', error);
    throw error; // 에러 발생 시 에러를 던짐
  }
};

// 발주 취소 API 함수
export const cancelOrder = async (orderId: number) => {
  try {
    const response = await axiosInstance.delete(
      `/api/order/company/detail/${orderId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Error cancelling order');
  }
};

// 발주 수정 API 함수
export const updateOrder = async (orderId: number, data: UpdateOrderData) => {
  try {
    const response = await axiosInstance.put(
      `/api/order/company/order/${orderId}`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error('Error updating order:', error);
    throw error;
  }
};

export const getObtainsVehicle = async (orderId: number) => {
  try {
    const response = await axiosInstance.get(
      `/api/order/driver/obtain/${orderId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error('Error getting obtains order');
  }
};

export const postObtainsOrder = async (
  orderId: number,
  orderData: ObtainsOrderDto,
) => {
  try {
    const response = await axiosInstance.post(
      `/api/order/driver/obtain/${orderId}`,
      orderData,
    );
    return response.data;
  } catch (error: any) {
    // 서버로부터 받은 에러 응답이 있을 경우 이를 그대로 던짐
    if (error.response) {
      throw error.response;
    }
    // 서버 응답이 없을 경우 기본 에러 메시지 던짐
    throw new Error('Error posting obtains order');
  }
};

export const getObtainsStateList = async (
  orderId: number,
): Promise<DetailOrderObtainDto[]> => {
  try {
    const response = await axiosInstance.get<DetailOrderObtainDto[]>(
      `/api/order/company/detail/${orderId}`,
    );
    return response.data;
  } catch (error) {
    throw error; // 에러를 던져 호출자가 처리하도록 함
  }
};

export const getObtainsStateSearch = async (
  orderId: number,
  vehicle?: string | null,
): Promise<DetailOrderObtainDto[]> => {
  try {
    const response = await axiosInstance.get<DetailOrderObtainDto[]>(
      `/api/order/company/obtain/${orderId}`,
      {
        params: { vehicle: vehicle ?? undefined }, // vehicle이 있을 경우 쿼리 파라미터로 전달
      },
    );
    return response.data;
  } catch (error) {
    throw error; // 에러를 던져 호출자가 처리하도록 함
  }
};

export const postOrderNotification = async (
  data: CreateObtainNotificationDto,
): Promise<ServerResponse> => {
  try {
    const response = await axiosInstance.post(
      `/api/order/company/notification`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error; // 에러를 던져 호출자가 처리하도록 함
  }
};
